// Tabla mono compacta con flash-on-change: cada fila recuerda su serialización
// previa (por rowKey) y si cambió entre polls dispara la animación row-flash —
// el efecto "tablero en vivo" del inspector.

import { useRef, type ReactNode } from 'react';

export interface Column<T> {
  header: string;
  cell: (row: T) => ReactNode;
  /** Clase extra para la celda (ej: text-right). */
  className?: string;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  empty,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  empty: string;
}) {
  // Mapa rowKey → JSON previo. Persistente entre renders, sin estado (no
  // queremos re-render extra: la clase de animación se decide al render).
  const prevRef = useRef<Map<string, string>>(new Map());
  const seen = new Map<string, string>();

  if (rows.length === 0) {
    prevRef.current = seen;
    return <p className="text-[11px] text-ink-subtle">{empty}</p>;
  }

  const body = rows.map((row) => {
    const key = rowKey(row);
    const json = JSON.stringify(row);
    seen.set(key, json);
    const prev = prevRef.current.get(key);
    const changed = prev !== undefined && prev !== json;
    return (
      <tr
        // key compuesta con el hash del contenido: al cambiar la fila cambia la
        // key → React remonta el <tr> → la animación CSS se re-dispara.
        key={changed ? `${key}#${json.length}${json.charCodeAt(json.length - 5) || 0}` : key}
        className={`border-b border-surface-border/60 last:border-0 ${changed ? 'animate-row-flash' : ''}`}
      >
        {columns.map((col, i) => (
          <td key={i} className={`px-2 py-1 align-top ${col.className ?? ''}`}>
            {col.cell(row)}
          </td>
        ))}
      </tr>
    );
  });
  prevRef.current = seen;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr className="border-b border-surface-border-strong text-left">
            {columns.map((col, i) => (
              <th
                key={i}
                className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-subtle ${col.className ?? ''}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  );
}
